import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { api, clearSessionToken } from '../lib/api';
import { showInterstitialNow, APP_OPEN_AD_KEY } from '../lib/admob';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me', { validateStatus: (s) => s < 500 });
      if (data && data.user_id) setUser(data);
      else setUser(null);
    } catch (e) {
      console.warn('[Auth] /me failed', e);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me check; AuthCallback handles it
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  // ─── App-open interstitial ──────────────────────────────────────────────
  // When the user opens the app and an existing session is auto-restored,
  // show a one-shot full-screen ad. Throttled to once every 30 minutes so
  // users who frequently re-open the app are not spammed. Skipped for
  // premium subscribers.
  useEffect(() => {
    if (loading) return;
    if (!user) return;            // not logged in — Login.jsx handles its own ads
    if (user.is_premium) return;  // never advertise to paying subscribers
    showInterstitialNow({ throttleKey: APP_OPEN_AD_KEY }).catch(() => {});
  }, [loading, user]);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch (e) { console.warn('[Auth] logout failed', e); }
    clearSessionToken();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, refresh, logout }),
    [user, loading, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
